import { StyledOrderBySelector } from './style';
import { Select, SelectOption } from '../Select';
import { useIntl } from 'react-intl';
import useMarketType from '../../../utils/useMarketType';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

export type OrderDirection = 'asc' | 'desc' | undefined;

const DIRECTION_CYCLE: OrderDirection[] = [undefined, 'asc', 'desc'];

export interface OrderBySelectorProps {
  value?: string;
  direction?: OrderDirection;
  allowClear?: boolean;
  showExtraOrders?: boolean;
  onChange?: (orderBy?: string, direction?: OrderDirection) => void;
}

export const OrderBySelector = ({
  value,
  direction = undefined,
  allowClear = false,
  showExtraOrders,
  onChange,
}: OrderBySelectorProps) => {
  const intl = useIntl();
  const { isEmarket } = useMarketType();

  const orderBy = isEmarket
    ? [{ name: 'newest' }, { name: 'name' }]
    : [{ name: 'price' }, { name: 'price_package' }, { name: 'name' }];

  const cycleDirection = () => {
    if (!value) return;
    const currentIndex = DIRECTION_CYCLE.indexOf(direction);
    const nextIndex = (currentIndex + 1) % DIRECTION_CYCLE.length;
    onChange?.(value, DIRECTION_CYCLE[nextIndex]);
  };

  const handleSelect = (selectedValue: string | number) => {
    if (String(selectedValue) === value) {
      cycleDirection();
    }
  };

  const handleChange = (newValue: string | undefined) => {
    if (newValue) {
      onChange?.(newValue, undefined);
    } else {
      onChange?.(undefined, undefined);
    }
  };

  const DirectionIcon = () => {
    if (direction === 'asc') return <ArrowUpOutlined />;
    if (direction === 'desc') return <ArrowDownOutlined />;
    return null;
  };

  return (
    <StyledOrderBySelector>
      <div className="txt-icon">
        <div className="text">
          {intl.formatMessage({ id: 'top_filter.filter_by' })}:
        </div>
        <div className="icon">
          <Select
            allowClear={allowClear}
            showSearch={false}
            variant="borderless"
            virtual={false}
            dropdownStyle={{ minWidth: '170px' }}
            value={value}
            onChange={handleChange}
            onSelect={handleSelect}
            name="order_by"
            children={orderBy.map((opt, index) => (
              <SelectOption
                value={opt.name}
                title={opt.name}
                key={`${index}-${opt.name}`}
              >
                {intl.formatMessage({ id: `top_filter.${opt.name}` })}
              </SelectOption>
            ))}
          />
          {value && (
            <button
              type="button"
              className="direction-toggle"
              onClick={cycleDirection}
              aria-label={direction === 'asc' ? 'Ascending' : direction === 'desc' ? 'Descending' : 'No sort'}
            >
              <DirectionIcon />
            </button>
          )}
        </div>
      </div>
    </StyledOrderBySelector>
  );
};
